import { Dropdown } from "Common/Components/Dropdown";
import { useAppServices } from "hook/services";
import {
  ArrowDownToLine,
  CheckCircle,
  Eye,
  FileEdit,
  MoreHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import FieldEditModal from "../Modal/Edit";
import { Column } from "Common/Components/CustomTable/types";
import { useParams } from "react-router-dom";
import moment from "moment";

export default function FieldsData() {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [fields, setFields] = useState<any>([]);
  const [folders, setFolders] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editData, setEditData] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const toggleEditModal = () => setShowEditModal((prev) => !prev);

  const getFields = async (page: number = 1, limit: number = itemsPerPage) => {
    setLoading(true);

    const { response, error } = await AppService.custom_field.field.filter({
      query: `locationId=${locationId}&page=${page}&limit=${limit}`,
    });

    if (response) {
      setFields(response.data || []);
      setTotalItems(response.pagination?.total || 0);
      setTotalPages(response.pagination?.pages || 0);
      setCurrentPage(response.pagination?.page || page);
    } else {
      setFields([]);
      setTotalItems(0);
      setTotalPages(0);
    }
    setLoading(false);
  };

  const getFolders = async () => {
    const { response, error } = await AppService.custom_field.folder.filter({
      query: `locationId=${locationId}`,
    });
    if (response) {
      setFolders(response.data || []);
    } else {
      setFolders([]);
    }
  };

  let pageLoad = true;
  const onload = (page: number = 1) => {
    if (pageLoad) {
      getFields(page);
      getFolders();
      pageLoad = false;
    }
  };

  useEffect(() => {
    onload(1);
  }, []);

  const handlePageChange = (page: number) => {
    onload(page);
  };

  const handleDelete = async (_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { response, error } = await AppService.custom_field.field.delete({
          query: `_id=${_id}`,
        });

        if (response) {
          Swal.fire("Deleted!", "The record has been deleted.", "success");
          // Reload current page after deletion
          onload(currentPage);
        } else {
          Swal.fire(
            "Error!",
            error?.message || "Something went wrong.",
            "error"
          );
        }
      } catch (err: any) {
        Swal.fire("Error!", err.message || "Delete failed.", "error");
      }
    }
  };

  const columns: Column[] = [
    {
      key: "name",
      label: "Name",
      sortable: false, // Disable client-side sorting when using server-side pagination
      render: (value: string) => (
        <h6 className="font-medium text-gray-900 dark:text-white">{value}</h6>
      ),
    },
    {
      key: "fieldKey",
      label: "Field Key",
      sortable: false,
      render: (value: string) => (
        <span className="text-gray-700 dark:text-gray-300">{value}</span>
      ),
    },
    {
      key: "dataType",
      label: "Data Type",
      sortable: false,
      render: (value: string) => (
        <span className="capitalize text-gray-700 dark:text-gray-300">
          {value.replaceAll("_", " ")}
        </span>
      ),
    },
    {
      key: "object",
      label: "Object Type",
      sortable: false,
      render: (value: string) => (
        <span className="capitalize text-gray-700 dark:text-gray-300">
          {value}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created On",
      sortable: false,
      render: (value: string) => (
        <span className="text-gray-500 text-sm">
          {moment(value).format("MMM DD, YYYY")}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      className: "text-right",
      render: (_: any, row: any) => (
        <div className="flex items-center gap-1.5">
          <button
            title="Edit record"
            onClick={() => {
              toggleEditModal();
              setEditData(row);
            }}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 dark:hover:bg-amber-900 transition-all duration-150"
          >
            <FileEdit className="w-3.5 h-3.5" />
          </button>

          <button
            title="Delete record"
            onClick={() => handleDelete(row._id)}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-300 dark:hover:bg-rose-900 transition-all duration-150"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return {
    loading: loading,
    fields: fields,
    columns: columns,
    folders: folders,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    handleRefresh: () => onload(currentPage),
    handlePageChange,
    EditModel: (
      <FieldEditModal
        handleRefresh={() => onload(currentPage)}
        folders={folders}
        show={showEditModal}
        toggle={toggleEditModal}
        editData={editData}
      />
    ),
  };
}
